import mixpanel from 'mixpanel-browser';
import { getUserRole } from 'common/utils/permissions';

export function initMixpanel() {
  if (!process.env.CROWE_MIXPANEL_TOKEN) {
    console.warn('Mixpanel token is missing. Initialization skipped.');
    return;
  }
  try {
    mixpanel.init(process.env.CROWE_MIXPANEL_TOKEN, {
      debug: false,
      ignore_dnt: true,
      persistence: 'localStorage',
    });
  } catch (error) {
    console.error('Mixpanel initialization failed:', error);
  }
}

export const resetMixpanel = () => {
  try {
    mixpanel.reset();
  } catch (error) {
    console.error('Mixpanel reset failed:', error);
  }
};

// Track event with fallback user id and timestamp. mixpanel.get_distinct_id is used to assign a unique id in mixpanel. It will merge with the portal user id when executing mixpanel.identify() in UserTracker.jsx
export const trackEvent = (eventName, properties = {}) => {
  const fallbackUserId = mixpanel.get_distinct_id?.() || 'unknown';

  try {
    mixpanel.track(eventName, {
      ...properties,
      user_id: properties.user_id || fallbackUserId,
      timestamp: properties.timestamp || new Date().toISOString(),
    });
  } catch (error) {
    console.error('Mixpanel tracking failed:', error);
  }
};

export const trackDownload = (title, url, resourceIsVideo, link) => {
  if (resourceIsVideo) {
    trackEvent('Video Viewed', {
      Title: title,
      Url: url,
    });
    window.location.href = link;
    return;
  }

  trackEvent('Resource Downloaded', {
    Title: title,
    Url: url,
  });

  window.open(url, '_blank');
};

export const trackBanner = title => {
  trackEvent('Banner Clicked', {
    Title: title,
  });
};

export const trackSearchBar = (mode, value) => {
  trackEvent('Search Bar Used', {
    Mode: mode,
    Value: value,
  });
};

export const trackEventClick = (name, date, place) => {
  trackEvent('Event Clicked', {
    Name: name,
    'Event Date': date,
    Place: place,
  });
};

export const trackArticle = (title, tags) => {
  trackEvent('Article Clicked', {
    Title: title,
    Tags: tags,
  });
};

export const trackLogin = (token, userId) => {
  if (typeof mixpanel?.track === 'function' && typeof mixpanel?.identify === 'function') {
    try {
      const role = getUserRole(token) || 'Unknown'; // Fallback to avoid undefined Role

      mixpanel.identify(userId);
      mixpanel.track('User Logged In', {
        Role: role,
        user_id: userId,
      });
    } catch (error) {
      console.error('Mixpanel tracking failed:', error);
    }
  } else {
    console.warn('Mixpanel is not available, skipping login tracking.');
  }
};

export const trackRegistration = firmName => {
  trackEvent('User Registered', {
    'Firm Name': firmName,
  });
};

export const trackUserApproved = approvedUserId => {
  trackEvent('User Approved', {
    'Approved User': approvedUserId,
  });
};

export const trackUserRejected = rejectedUserId => {
  trackEvent('User Rejected', {
    'Rejected User': rejectedUserId,
  });
};

export const trackUserDeactivated = deactivatedUserId => {
  trackEvent('User Deactivated', {
    'Deactivated User': deactivatedUserId,
  });
};

export const trackUserReactivated = reactivatedUserId => {
  trackEvent('User Reactivated', {
    'Reactivated User': reactivatedUserId,
  });
};

export const trackPrivilegesGranted = grantedUserId => {
  trackEvent('Privileges Granted', {
    'Granted User': grantedUserId,
  });
};

export const trackPrivilegesRevoked = grantedUserId => {
  trackEvent('Privileges Revoked', {
    'Revoked User': grantedUserId,
  });
};

export const trackFirmFilters = (filterName, value) => {
  trackEvent('GD Member Firms Filter Used', {
    'Filter Name': filterName,
    'Filter Value': value?.value || 'N/A',
  });
};

export const trackMemberFilters = (filterName, value) => {
  trackEvent('GD People Filter Used', {
    'Filter Name': filterName,
    'Filter Value': value?.value || 'N/A',
  });
};

export const trackDesksFilters = (filterName, value) => {
  trackEvent('GD Foreign Desks Filter Used', {
    'Filter Name': filterName,
    'Filter Value': value?.value || 'N/A',
  });
};

export const trackClearFilters = (eventName) => {
  trackEvent(eventName, {});
};

export const trackSocialPost = () => {
  trackEvent('Social Post Submitted', {});
};